<template>
  <div v-show="isActive" class="map-tool-window upload-tool-window" id="uploadShpKmzID">
    <!-- HEADER -->
    <div class="map-tool-window-header" id="uploadShpKmzIDheader">
      <p class="map-tool-window-header-title">Cargar Capas</p>
      <div class="map-tool-window-header-actions">
        <button class="control-btn" @click="exitUploadShpKmz">
          <!-- <font-awesome-icon icon="times" /> -->
        <svg viewBox="0 0 24 24"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>
        </button>
      </div>
    </div>

    <!-- BOTÓN DE SUBIDA -->
    <div class="map-tool-window-body">
      <div class="upload-button-container">
        <label for="file-upload" class="upload-icon-btn" title="Subir archivo">
          <font-awesome-icon icon="upload" />
        </label>

        <input type="file" id="file-upload" multiple :accept="acceptedExtensions" style="display: none" @change="selectFile" />
      </div>

      <div v-if="pendingFile" class="import-name-form">
        <label for="import-layer-name">Nombre de la capa</label>
        <input id="import-layer-name" ref="layerNameInput" v-model.trim="pendingName" type="text" maxlength="120"
          @keyup.enter="confirmFileUpload" placeholder="Ingrese un nombre" />
        <small>Archivo: {{ pendingFile.name }}</small>
        <small v-if="pendingInspection">Formato detectado: {{ pendingInspection.format }}</small>

        <template v-if="pendingInspection && pendingInspection.needsCrs">
          <label for="import-source-crs">Proyección del SHP</label>
          <select id="import-source-crs" v-model="importOptions.sourceCrs">
            <option value="EPSG:4326">EPSG:4326 — WGS84</option>
            <option value="EPSG:3857">EPSG:3857 — Web Mercator</option>
          </select>
          <small>Un SHP suelto no contiene atributos ni información de proyección.</small>
        </template>

        <template v-if="pendingInspection && pendingInspection.needsParquetMapping">
          <label for="parquet-mode">Geometría de Parquet</label>
          <select id="parquet-mode" v-model="importOptions.parquet.mode">
            <option value="coordinates">Longitud / latitud</option>
            <option value="wkt">Columna WKT</option>
            <option value="wkb">Columna WKB</option>
          </select>
          <template v-if="importOptions.parquet.mode === 'coordinates'">
            <label>Longitud</label>
            <select v-model="importOptions.parquet.longitude"><option v-for="column in pendingInspection.columns" :key="`lon-${column}`" :value="column">{{ column }}</option></select>
            <label>Latitud</label>
            <select v-model="importOptions.parquet.latitude"><option v-for="column in pendingInspection.columns" :key="`lat-${column}`" :value="column">{{ column }}</option></select>
          </template>
          <template v-else>
            <label>Columna geométrica</label>
            <select v-model="importOptions.parquet.geometryColumn"><option v-for="column in pendingInspection.columns" :key="`geometry-${column}`" :value="column">{{ column }}</option></select>
          </template>
          <label>Proyección de la geometría</label>
          <select v-model="importOptions.sourceCrs"><option value="EPSG:4326">EPSG:4326</option><option value="EPSG:3857">EPSG:3857</option></select>
        </template>
        <template v-else-if="pendingInspection && pendingInspection.geo && Object.keys(pendingInspection.geo.columns || {}).length > 1">
          <label>Geometría de GeoParquet</label>
          <select v-model="importOptions.geometryColumn"><option v-for="column in Object.keys(pendingInspection.geo.columns)" :key="column" :value="column">{{ column }}</option></select>
        </template>

        <div v-if="pendingWarnings.length" class="import-warnings">
          <strong>Advertencias</strong>
          <small v-for="warning in pendingWarnings" :key="warning">• {{ warning }}</small>
        </div>
        <div class="import-name-actions">
          <button class="name-btn secondary" type="button" @click="cancelPendingFile">Cancelar</button>
          <button class="name-btn primary" type="button" :disabled="!canConfirmImport" @click="confirmFileUpload">{{ importButtonLabel }}</button>
        </div>
      </div>

      <!-- LISTA DE ARCHIVOS -->
      <div class="file-list">
        <div v-if="loading" class="loading-container">
          <font-awesome-icon icon="spinner" spin />
          <span>Cargando...</span>
        </div>
        
        <div v-else-if="uploadedFiles.length > 0">
          <div v-for="file in uploadedFiles" :key="file.rowId" class="file-item">
            <div class="file-details">
              <font-awesome-icon icon="file-alt" />
              <span class="file-name">{{ displayFileName(file) }}</span>
              <span
                v-if="file.manifest && file.manifest.warnings && file.manifest.warnings.length"
                class="file-warning"
                tabindex="0"
                :aria-label="file.manifest.warnings.join('. ')"
              >
                ⚠
                <span class="file-warning-tooltip" role="tooltip">{{ file.manifest.warnings.join('\n') }}</span>
              </span>
            </div>

            <div class="file-actions">
              <input
                type="checkbox"
                class="layer-checkbox"
                v-model="file.active"
                @change="toggleLayer(file)"
                title="Mostrar/Ocultar capa"
              />

              <button class="btn btn-action delete-btn" @click="confirmDeleteFile(file)">
                <font-awesome-icon icon="trash-alt" />
              </button>
            </div>
          </div>
        </div>

        <div v-else class="no-files">
          <span class="no-files-icon" aria-hidden="true">
            <font-awesome-icon icon="file-alt" />
          </span>

          <span class="no-files-title">
            No hay archivos cargados
          </span>

          <small class="no-files-description">
            Sube SHP, KMZ, KML, GPX, GeoJSON o (Geo)Parquet para visualizarlo.
          </small>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { draggableDiv } from "@/components/mixin/draggableDiv.js";
import { LayerService } from "@/services/layers/layerService.js";
import { attributesTableService } from "@/services/layers/attributesTable.js";
import {
  createImportedDescriptor,
  fileDatasets,
  importedLayerRegistry,
} from "@/services/layers/importedLayerRegistry.js";
import {
  importManifest,
  inspectImportedFile,
  parseImportedFile,
} from "@/services/layers/importedFileParser.js";
import { IMPORT_ACCEPT, importSelectionError } from "@/services/layers/importSupport.js";
import JSZip from "jszip";

const defaultImportOptions = () => ({
  sourceCrs: "EPSG:4326",
  geometryColumn: "",
  parquet: { mode: "coordinates", longitude: "", latitude: "", geometryColumn: "" },
});

export default {
  name: "UploadShpKmz",
  mixins: [draggableDiv],
  data() {
    return {
      uploadedFiles: [],
      loading: false,
      loadedMapId: null,
      pendingFile: null,
      pendingName: "",
      pendingInspection: null,
      parsedPending: null,
      warningsReviewed: false,
      importOptions: defaultImportOptions(),
      pendingAbortController: null,
    };
  },

  computed: {
    // El explorador de archivos solo ofrece lo que el importador sabe leer
    // (QA-184).
    acceptedExtensions() {
      return IMPORT_ACCEPT;
    },
    isActive() {
      return this.$store.state.mapTools.shpKmzUpload.active;
    },
    importedLayers() {
      return this.$store.getters.getImportedLayers || [];
    },
    canConfirmImport() {
      if (!this.pendingFile || !this.pendingName.trim() || this.loading || !this.pendingInspection) return false;
      if (this.pendingInspection.needsParquetMapping) {
        const mapping = this.importOptions.parquet;
        if (mapping.mode === "coordinates" && (!mapping.longitude || !mapping.latitude)) return false;
        if (mapping.mode !== "coordinates" && !mapping.geometryColumn) return false;
      }
      return true;
    },
    importButtonLabel() {
      if (!this.parsedPending) return "Importar";
      if (this.parsedPending && this.pendingWarnings.length && !this.warningsReviewed) return "Confirmar e importar";
      return "Importar capa";
    },
    pendingWarnings() {
      if (!this.parsedPending) return [];
      return Array.from(new Set(this.parsedPending.datasets.reduce((all, dataset) => all.concat(dataset.warnings || []), [])));
    },
  },

  mounted() {
    this.makeDraggable("uploadShpKmzID");
    this.getFilesByMap();
  },

  watch: {
    "$route.params.idMap"() {
      this.getFilesByMap();
    },
    importedLayers: {
      deep: true,
      handler(layers) {
        this.uploadedFiles.forEach(file => {
          const descriptor = layers.find(layer => layer.id === file.descriptorId);
          file.active = !!(descriptor && descriptor.selected);
        });
      },
    },
  },

  methods: {
    displayFileName(file) {
      return file.displayName || String(file?.name || "").replace(/\.[^.]+$/, "");
    },
    descriptorFor(file, selected = false) {
      return createImportedDescriptor(file, selected, file.dataset || null);
    },
    async getFilesByMap() {
      this.loading = true;
      try {
        const idMapa = this.$route.params.idMap;
        if (this.loadedMapId && this.loadedMapId !== idMapa) {
          importedLayerRegistry.list().forEach(entry => {
            LayerService.destroyImportedLayer(entry.descriptor.id);
            LayerService.clearImportedFileCache(entry.descriptor.fileId);
            this.$store.dispatch("removeActiveLayer", entry.descriptor);
          });
          importedLayerRegistry.clear();
        }
        this.loadedMapId = idMapa;
        const files = await LayerService.getUploadedFilesByMap(idMapa);
        const safeFiles = Array.isArray(files) ? files : [];
        const expanded = safeFiles.reduce((rows, file) => rows.concat(fileDatasets(file).map(dataset => ({ ...file, dataset }))), []);
        const descriptors = expanded.map(file => {
          const candidate = this.descriptorFor(file);
          return this.descriptorFor(file, LayerService.layerExists(candidate.id));
        });
        this.$store.dispatch("setImportedLayers", descriptors);
        this.uploadedFiles = expanded.map(file => {
          const descriptor = this.descriptorFor(file);
          return {
          ...file,
          rowId: descriptor.id,
          descriptorId: descriptor.id,
          displayName: descriptor.name,
          active: descriptors.some(layer => layer.id === descriptor.id && layer.selected),
        }; });
        return this.uploadedFiles;
      } finally {
        this.loading = false;
      }
    },
    exitUploadShpKmz() {
      this.$store.dispatch("setMapToolSelection", {
        tool: "shpKmzUpload",
        active: false
      });
    },
    
    async selectFile(event) {
      const selected = Array.from(event.target.files || []);
      event.target.value = "";
      if (!selected.length) return;
      this.loading = true;
      try {
        // Filtro previo: el usuario puede saltarse el accept del input eligiendo
        // "Todos los archivos" en el explorador, y un .dbf/.prj suelto llegaba
        // hasta el parser solo para morir con un error genérico (QA-184).
        const selectionError = importSelectionError(selected.map(item => item.name));
        if (selectionError) throw new Error(selectionError);

        let file = selected[0];
        if (selected.length > 1) {
          const zip = new JSZip();
          selected.forEach(item => zip.file(item.name, item));
          const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
          file = new File([blob], `${selected.find(item => item.name.toLowerCase().endsWith(".shp")).name.replace(/\.shp$/i, "")}.zip`, { type: "application/zip" });
        }
        this.pendingFile = file;
        this.pendingName = file.name.replace(/\.[^.]+$/, "");
        this.pendingInspection = await inspectImportedFile(file);
        this.parsedPending = null;
        this.warningsReviewed = false;
        this.importOptions = defaultImportOptions();
        this.configureInspectionDefaults();
        this.$nextTick(() => this.$refs.layerNameInput?.focus());
      } catch (error) {
        this.cancelPendingFile();
        this.$store.dispatch("toast/show", { message: error.message || "No fue posible analizar el archivo", type: "error" });
      } finally {
        this.loading = false;
      }
    },
    configureInspectionDefaults() {
      const columns = this.pendingInspection?.columns || [];
      const find = patterns => columns.find(column => patterns.includes(String(column).toLowerCase())) || "";
      this.importOptions.parquet.longitude = find(["longitude", "lon", "lng", "x"]);
      this.importOptions.parquet.latitude = find(["latitude", "lat", "y"]);
      this.importOptions.parquet.geometryColumn = find(["geometry", "geom", "wkt", "wkb"]);
      this.importOptions.geometryColumn = this.pendingInspection?.primaryGeometry || "";
    },
    cancelPendingFile() {
      if (this.pendingAbortController) this.pendingAbortController.abort();
      this.pendingAbortController = null;
      if (this.parsedPending) this.parsedPending.datasets.forEach(dataset => { if (dataset.cleanup) dataset.cleanup(); });
      this.pendingFile = null;
      this.pendingName = "";
      this.pendingInspection = null;
      this.parsedPending = null;
      this.warningsReviewed = false;
      this.importOptions = defaultImportOptions();
    },
    async confirmFileUpload() {
      const file = this.pendingFile;
      const displayName = this.pendingName.trim();
      if (!file || !displayName || this.loading) return;
      const idMapa = this.$route.params.idMap;
      
      this.loading = true;
      try {
        if (!this.parsedPending) {
          this.pendingAbortController = new AbortController();
          this.parsedPending = await parseImportedFile(file, { ...this.importOptions, signal: this.pendingAbortController.signal });
          this.pendingAbortController = null;
          if (this.pendingWarnings.length) return;
        }
        if (this.pendingWarnings.length && !this.warningsReviewed) this.warningsReviewed = true;
        const previousIds = new Set(this.uploadedFiles.map(item => String(item.id)));
        const extension = (file.name.match(/\.[^.]+$/) || [""])[0].toLowerCase();
        const safeName = displayName.replace(/[^a-zA-Z0-9._-]/g, "_").replace(/^\.+|\.+$/g, "");
        if (!safeName) throw new Error("Ingrese un nombre válido para la capa");
        const uploadFile = new File([file], `${safeName}${extension}`, { type: file.type, lastModified: file.lastModified });
        const manifest = importManifest(this.parsedPending, displayName);
        const uploadResult = await LayerService.uploadShpKmz(uploadFile, idMapa, manifest);
        const files = await this.getFilesByMap();
        const inserted = uploadResult && (uploadResult.InsertedID || uploadResult.insertedId || uploadResult.id);
        const insertedId = inserted && typeof inserted === "object" ? inserted.$oid : inserted;
        const uploaded = files.filter(item => String(item.id) === String(insertedId));
        const uploadedRows = uploaded.length ? uploaded : files.filter(item => !previousIds.has(String(item.id)));
        if (!uploadedRows.length) throw new Error("El archivo se guardó, pero no fue posible identificar la nueva capa");
        const parsed = this.parsedPending;
        for (let index = 0; index < uploadedRows.length; index += 1) {
          const row = uploadedRows[index];
          const dataset = parsed.datasets.find(item => item.key === row.dataset?.key) || parsed.datasets[index] || parsed.datasets[0];
          await this.activateFile(row, index === 0, dataset);
        }
        this.$store.dispatch("toast/show", { message: `${uploadedRows.length} capa(s) importada(s) correctamente`, type: "success" });
        this.parsedPending = null;
        this.cancelPendingFile();
      } catch (e) {
        this.pendingAbortController = null;
        console.error("Error upload:", e);
        this.$store.dispatch("toast/show", { message: e.message || "No se pudo importar el archivo", type: "error" });
      } finally {
        this.loading = false;
      }
    },
    confirmDeleteFile(file) {
      this.$modal.show("dialog", {
        title: "Eliminar archivo",
        text: `¿Eliminar "${this.displayFileName(file)}"? Esta acción no se puede deshacer.`,
        buttons: [
          { title: "Cancelar" },
          {
            title: "Eliminar",
            class: "vue-dialog-button upload-shp-kmz-delete-button",
            handler: () => {
              this.$modal.hide("dialog");
              this.deleteFile(file.id);
            },
          },
        ],
      });
    },
    async deleteFile(fileId) {
      this.loading = true;
      try {
        await LayerService.deleteFileByID(fileId);
        const descriptors = this.$store.getters.getImportedLayers.filter(layer => layer.fileId === fileId);
        descriptors.forEach(descriptor => {
          LayerService.destroyImportedLayer(descriptor.id);
          this.$store.dispatch("removeActiveLayer", descriptor);
          this.$store.dispatch("removeImportedLayer", descriptor.id);
          if (this.$store.getters["attributeTable/layer"]?.id === descriptor.id) {
            this.$store.dispatch("attributeTable/closeTable");
          }
        });
        LayerService.clearImportedFileCache(fileId);
        console.log("Archivo eliminado exitosamente");
        await this.getFilesByMap(); // Refresh list
      } catch (e) {
        console.error("Error delete:", e);
        this.$store.dispatch("toast/show", { message: e.message || "No se pudo eliminar el archivo", type: "error" });
      } finally {
        this.loading = false;
      }
    },
    async toggleLayer(file) {
      if (file.active) {
        try {
          this.loading = true;
          await this.activateFile(file, false);
        } catch(e) {
          console.error("Error toggling layer:", e);
          file.active = false; // Revert checkbox if failed
          this.$store.dispatch("toast/show", { message: e.message || "No se pudo activar la capa", type: "error" });
        } finally {
          this.loading = false;
        }
      } else {
        const descriptor = this.$store.getters.getImportedLayers.find(layer => layer.id === file.descriptorId);
        if (descriptor) this.deactivateDescriptor(descriptor);
      }
    },
    async activateFile(file, openTable, parsedDataset = null) {
      const descriptor = this.descriptorFor(file, true);
      if (openTable) {
        this.$store.dispatch("setMapToolSelection", { tool: "attributesTable", active: true });
        await this.$store.dispatch("attributeTable/openTable", { layer: descriptor, attributes: [], totalFeatures: 0 });
      }
      try {
        const entry = parsedDataset
          ? LayerService.loadParsedImportedLayer(descriptor, parsedDataset)
          : await LayerService.loadLayerFromBackend(file, descriptor);
        if (!entry) return null;
        entry.descriptor = descriptor;
        this.$store.dispatch("upsertImportedLayer", descriptor);
        this.$store.dispatch("addActiveLayer", descriptor);
        const listedFile = this.uploadedFiles.find(item => item.descriptorId === descriptor.id);
        if (listedFile) listedFile.active = true;
        if (openTable) await attributesTableService.openTable(descriptor);
        return entry;
      } catch (error) {
        if (openTable && this.$store.getters["attributeTable/layer"]?.id === descriptor.id) {
          this.$store.dispatch("attributeTable/closeTable");
        }
        throw error;
      }
    },
    deactivateDescriptor(descriptor) {
      LayerService.deactivateImportedLayer(descriptor.id);
      this.$store.dispatch("upsertImportedLayer", { ...descriptor, selected: false });
      this.$store.dispatch("removeActiveLayer", descriptor);
      importedLayerRegistry.restore(descriptor.id);
      if (this.$store.getters["attributeTable/layer"]?.id === descriptor.id) {
        this.$store.dispatch("attributeTable/closeTable");
      }
    },
  }
};
</script>

<style scoped lang="scss">
.upload-tool-window{
  left: 78vw;
  top: 4em;
}

.upload-button-container {
  display: flex;
  justify-content: center;

  padding-bottom: 14px;
  margin-bottom: 12px;

  border-bottom: 1px solid var(--color-line);
}

.upload-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;

  width: 100%;
  min-height: 46px;
  padding: 10px 14px;

  background: var(--color-mist);
  border: 1px dashed var(--color-steel);
  border-radius: 6px;

  color: var(--color-navy);
  cursor: pointer;

  font-size: 0.82rem;
  font-weight: 700;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  /* Agrega texto sin modificar el template */
  &::after {
    content: "Seleccionar archivo geoespacial";
  }

  &:hover {
    background: var(--color-fog);
    border-color: var(--color-navy);
    box-shadow: 0 2px 6px rgba(14, 42, 64, 0.1);
  }

  svg {
    font-size: 1rem;
  }
}

.import-name-form {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 12px;
  padding: 10px;
  background: var(--color-mist);
  border: 1px solid var(--color-line);
  border-radius: 5px;

  label { color: var(--color-navy); font-size: .78rem; font-weight: 700; }
  input, select { padding: 8px 9px; border: 1px solid var(--color-steel); border-radius: 4px; background: #fff; font-size: .82rem; }
  small { overflow: hidden; color: var(--color-steel); font-size: .68rem; text-overflow: ellipsis; white-space: nowrap; }
}

.import-warnings { display: flex; flex-direction: column; gap: 3px; padding: 8px; border: 1px solid #d5a72b; border-radius: 4px; background: #fff8dd; color: #725600; }

/* Tooltip propio en vez de solo el title nativo: el cursor "?" (cursor:help)
   por sí solo no explicaba nada — hay que poder ver el texto del warning. */
.file-warning {
  position: relative;
  flex-shrink: 0;
  color: #9a6b00;
  cursor: help;

  .file-warning-tooltip {
    position: absolute;
    z-index: 20;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);

    min-width: 160px;
    max-width: 240px;
    padding: 6px 9px;

    background: var(--color-graphite, #2c3e50);
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);

    color: #fff;
    font-size: 0.68rem;
    font-weight: 400;
    line-height: 1.4;
    text-align: left;
    white-space: pre-line;

    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease;
    pointer-events: none;
  }

  &:hover .file-warning-tooltip,
  &:focus .file-warning-tooltip,
  &:focus-visible .file-warning-tooltip {
    opacity: 1;
    visibility: visible;
  }
}

.import-name-actions { display: flex; justify-content: flex-end; gap: 7px; }
.name-btn { padding: 6px 10px; border: 1px solid var(--color-line); border-radius: 4px; cursor: pointer; font-size: .75rem; font-weight: 700; }
.name-btn.primary { background: var(--color-navy); color: #fff; }
.name-btn.secondary { background: #fff; color: var(--color-graphite); }
.name-btn:disabled { opacity: .5; cursor: not-allowed; }


.file-list {
  display: flex;
  flex-direction: column;
  gap: 8px;

  max-height: 320px;
  padding: 0;

  overflow-y: auto;
}

/* Cada archivo cargado */
.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  padding: 10px;

  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: 5px;

  font-size: 0.8rem;

  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: var(--color-fog);
    box-shadow: 0 2px 6px rgba(14, 42, 64, 0.08);
  }
}

.file-details {
  display: flex;
  align-items: center;
  gap: 8px;

  min-width: 0;
  flex: 0 1 auto;

  color: var(--color-steel);

  > svg {
    flex-shrink: 0;
    font-size: 0.95rem;
  }
}

.file-name {
  min-width: 0;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  color: var(--color-graphite);
  font-size: 0.78rem;
  font-weight: 600;
}


.file-actions {
  display: flex;
  align-items: center;
  gap: 8px;  
  flex-shrink: 0;
}

.layer-checkbox {
  width: 15px;
  height: 15px;

  accent-color: var(--color-navy);
  cursor: pointer;
}

.btn-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 28px;
  width: 28px;
  height: 28px;
  padding: 0;

  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;

  color: var(--color-steel);
  cursor: pointer;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}

.delete-btn:hover {
  background: rgba(178, 58, 46, 0.08);
  border-color: rgba(178, 58, 46, 0.2);
  color: var(--color-danger);
}

.no-files {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;

  min-height: 110px;
  margin: 0;
  padding: 16px;

  text-align: center;
}

.no-files-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 40px;
  height: 40px;

  background: var(--color-mist);
  border: 1px solid var(--color-line);
  border-radius: 50%;

  color: var(--color-steel);

  svg {
    font-size: 1rem;
  }
}

.no-files-title {
  color: var(--color-navy);
  font-size: 0.8rem;
  font-weight: 700;
}

.no-files-description {
  max-width: 230px;

  color: var(--color-steel);
  font-size: 0.7rem;
  font-weight: 400;
  line-height: 1.4;
}

/* Estado de carga */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;

  min-height: 100px;
  padding: 18px;

  color: var(--color-steel);
  font-size: 0.78rem;

  svg {
    color: var(--color-navy);
    font-size: 1.1rem;
  }
}
</style>

<style>
/* Sin scope a propósito: el diálogo genérico de vue-js-modal (<v-dialog/>,
   App.vue) se monta en su propio contenedor dinámico fuera del árbol de
   este componente, así que un <style scoped> de acá nunca le llegaría. */
.upload-shp-kmz-delete-button {
  color: var(--color-danger, #b23a2e);
  font-weight: 700;
}
</style>
